import React, { useState, useEffect, useRef } from "react";
import Loader from "./Loader";
import PropTypes from "prop-types";
import apiService from "../utils/api";
import "./Subpage.css";
import Snackbar from '@mui/material/Snackbar';
import { CustomSelect } from '../components/modals/CreateBlankModal';  
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';


const Field = ({ label, value, editMode, onChange, type = "text", style}) => (
  <div>
    <div className="subpage-label">{label}</div>
    {editMode ? (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={type === "textarea" ? { width: "100%", minHeight: "30px",marginTop:'12px' } : {}}
      />
    ) : (
      <div className="subpage-value">{value || "N/A"}</div>
    )}
  </div>
);

const SelectField = ({ label, value, editMode, onChange, options, placeholder, contractData, fallbackLabel, multiSelect }) => (
  <div>
    <div className="subpage-label">{label}</div>
    {editMode ? (
      <CustomSelect
        options={options}
        value={value || (multiSelect ? [] : "")}
        onChange={onChange}
        placeholder={placeholder}
        multiSelect={multiSelect}
      />
    ) : (
      <div className="subpage-value">
        {multiSelect ? (
          (Array.isArray(value) ? value : (typeof value === "string" ? value.split(",").map(s => s.trim()) : []))
            .map(val => options.find(o => o.value === val)?.label || val)
            .join(", ") || "N/A"
        ) : (
          options.find(o => o.value === value)?.label ||
          fallbackLabel ||
          contractData?.RegionName ||
          value ||
          "N/A"
        )}
      </div>
    )}
  </div>
);

const Section = ({ title, isOpen, onToggle, children }) => (
  <div className="subpage-collapsible-section" style={{ marginBottom: "40px" }}>
    <div
      className="subpage-section-header"
      style={{ fontWeight: 500, fontSize: "16px" }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onToggle()}
      aria-expanded={isOpen}
    >
     <span className={`subpage-arrow ${isOpen ? "subpage-open" : ""}`}>
  {isOpen ? (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )}
</span>

      <span className="subpage-title">{title}</span>
    </div>
    {isOpen && <div className="subpage-section-body subpage-grid-2">{children}</div>}
  </div>
);

/* PropTypes for reusable components */
Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  editMode: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
};

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  editMode: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  placeholder: PropTypes.string,
  multiSelect: PropTypes.bool,
};

Section.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
};

const SubPage = ({ selectedNode, fromArchive }) => {
  const userRole = (sessionStorage.getItem("user_role") || "").replace(/\s+/g, "").toLowerCase();
  const fullAccessRoles = ['admin', 'legaluser', 'legalreviewer'];
  const hasFullAccess = fullAccessRoles.includes(userRole);

  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [versionLoading, setVersionLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({
    contractDetails: true,
    clientStatus: true,
    datesMetadata: true,
    billingMetadata: true,
  });
  const [activeTab, setActiveTab] = useState("contract");
  const [departments, setDepartments] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [versionData, setVersionData] = useState([]);
  const initialData = useRef(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [contractTypeOptions, setContractTypeOptions] = useState([]);   
  const [clientOptions, setClientOptions] = useState([]);
  const [billingCountries, setBillingCountries] = useState([]);
  const [billingTypes] = useState([
    { label: "Fixed", value: "fixed" },
    { label: "Time & Material", value: "time-n-material" },
    { label: "Milestone Based", value: "milestone-based" },
    { label: "Fixed Monthly", value: "fixed-monthly" }
  ]);
  const [executionTypes] = useState([
    { label: "Off Site", value: "off-site" },
    { label: "On-Site", value: "on-site" },
    { label: "Nearshore", value: "near-shore" }
  ]);
  const [currencies, setCurrencies] = useState([]);
  const [payRateCurrencies] = useState([
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' },
    { label: 'GBP', value: 'GBP' },
    { label: 'INR', value: 'INR' },
    { label: 'CAD', value: 'CAD' },
    { label: 'AUD', value: 'AUD' }
  ]);
  const [netTermOptions] = useState([
    { label: '30 days', value: '30days' },
    { label: '45 days', value: '45days' },
    { label: '60 days', value: '60days' },
    { label: '90 days', value: '90days' }
  ]);
  const [sowDetails, setSowDetails] = useState([]);

  
  useEffect(() => {
    const fetchContractData = async () => {
      if (!selectedNode?.id || selectedNode?.data?.type !== "File") {
        setError("Please select a file to view details");
        setLoading(false);
        return;
      }
      try {
        const cleanId = selectedNode.id
          .replace(/^(?:cont_|CONTRACT-)/u, "")
          .match(/\d{1,20}$/u)?.[0] || selectedNode.id;

        const data = await apiService.fetchData(`contracts/${cleanId}`);
        if (!data) throw new Error("No data returned from API");

        const normalizeTemplateName = (creationType, templateName) => {
          if (!templateName || ["n/a", "no template used"].includes(templateName?.toLowerCase())) {
            return "No Template Used";
          }
          if (["upload", "blank"].includes(creationType?.toLowerCase())) {
            return "No Template Used";
          }
          return templateName;
        };

        const normalizedTemplateName = normalizeTemplateName(data.creation_type, data.template_name);

        const processed = {
          ...data,
          template_name: normalizedTemplateName,
          is_favourite: data.favourite_status === "Favourite",
          is_deleted: data.contract_status_flag === "Deleted",
          contract_type_id: data.contract_type_id ?? data.contract_type,
          party_id: data.party_id ?? data.party_name,
          region_id: data.region_id,
          execution_type: data.execution_type ? (typeof data.execution_type === 'string' ? data.execution_type.split(',').map(s => s.trim()) : data.execution_type) : []
        };

        setContractData(processed);
        initialData.current = processed;
        setSowDetails(data.sow_details ? JSON.parse(data.sow_details) : []);
        setLoading(false);
      } catch (err) {
        console.error("API fetch error:", err);
        setError(`Failed to load contract data: ${err.message}`);
        setLoading(false);
      }
    };
    fetchContractData();
  }, [selectedNode]);

  
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [
          regionRes, statusRes, deptRes, projRes, tmplRes, contractTypeRes, clientRes,
          billingCountryRes, currencyRes
        ] = await Promise.all([
          apiService.fetchData("metadata/region"),
          apiService.fetchData("metadata/contract-status"),
          apiService.fetchData("metadata/department"),
          apiService.fetchData("ruddr/project"),
          apiService.fetchData("templates"),
          apiService.fetchData("metadata/contract-type"),
          apiService.fetchData("add_client"),
          apiService.fetchData("metadata/region"),
          apiService.fetchData("currencies")
        ]);

        setRegions(regionRes || []);
        setStatuses(statusRes || []);
        setDepartments(deptRes || []);

        setProjectOptions(projRes?.data?.map(p => ({ label: p.name, value: p.id })) || []);
        setContractTypeOptions(contractTypeRes?.map(c => ({ label: c.value, value: c.id })) || []);
        setTemplateOptions(tmplRes?.map(t => ({ label: t.template_name, value: t.template_id })) || []);
        setClientOptions(clientRes?.clients?.results?.map(c => ({
          label: c.clientName,
          value: c.client_id
        })) || []);
        
        setBillingCountries(billingCountryRes?.map(r => ({ label: r.value, value: r.id })) || []);
        setCurrencies(currencyRes?.map(c => ({ label: c.code || c.name, value: c.code || c.id })) || []);
      } catch (err) {
        console.error("Dropdown fetch error:", err);
      }
    };
    fetchDropdowns();
  }, []);

 
  useEffect(() => {
    const fetchVersionHistory = async () => {
      if (activeTab !== "datatable" || !contractData?.sharepointPath || !contractData?.sharepointName) return;

      setVersionLoading(true);
      try {
        const year = contractData.start_date ? new Date(contractData.start_date).getFullYear() : "2025";
        const clientIdentifier = contractData.clientName || "";
        const basePath = contractData.sharepointPath;
        const updatedPath = basePath.replace(/CLM\/Contracts\/[^/]+/, `CLM/Contracts/${year}/${clientIdentifier}`);
        const filePath = `Shared Documents/${updatedPath}/${contractData.sharepointName}`;
        const encodedPath = encodeURIComponent(filePath);

        const data = await apiService.fetchData(`get-version-history?file_path=${encodedPath}`);
        setVersionData(data?.status === "success" ? data.versions : []);
      } catch (error) {
        console.error("Error fetching version history:", error);
        setVersionData([]);
      } finally {
        setVersionLoading(false);
      }
    };
    fetchVersionHistory();
  }, [activeTab, contractData]);

 
  useEffect(() => {
    if (initialData.current) {
      setContractData({ ...initialData.current });
    }
    setEditMode(false);
  }, [selectedNode]);


  const handleChange = (field, value) => {
    const readOnlyFields = ['sharepointName', 'sharepointPath', 'creation_type', 'clientName'];
    if (!readOnlyFields.includes(field)) {
      setContractData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleEditToggle = () => setEditMode(true);
  const handleCancel = () => {
    setContractData({ ...initialData.current });
    setSowDetails(initialData.current.sow_details ? JSON.parse(initialData.current.sow_details) : []);
    setEditMode(false);
    setSnackbarMessage("Changes cancelled");
    setIsSuccess(false);
    setSnackbarOpen(true);
  };

  const handleSave = async () => {
    const userId = sessionStorage.getItem("user_id") || null;
    const currentDate = new Date().toISOString().split('T')[0];
    const cleanId = selectedNode.id.replace(/^(?:cont_|CONTRACT-)/u, "").match(/\d{1,20}$/u)?.[0] || selectedNode.id;

    if (!contractData.contract_title?.trim()) {
      setSnackbarMessage("Contract Title is required");
      setIsSuccess(false);
      setSnackbarOpen(true);
      setContractData({ ...initialData.current });
      return;
    }

    if (!contractData.description?.trim() || contractData.description.trim().length < 10) {
      setSnackbarMessage(!contractData.description?.trim()
        ? "Description is required"
        : "Description must be at least 10 characters long."
      );
      setIsSuccess(false);
      setSnackbarOpen(true);
      setContractData({ ...initialData.current });
      return;
    }

    const payload = { contract_id: cleanId, updated_by: userId, updated_at: currentDate };
    const updatedFields = [];

    Object.keys(contractData).forEach(key => {
      if (key === "execution_type") {
        const initialVal = Array.isArray(initialData.current[key]) ? initialData.current[key].join(",") : (initialData.current[key] || "");
        const currentVal = Array.isArray(contractData[key]) ? contractData[key].join(",") : (contractData[key] || "");
        if (initialVal !== currentVal) {
          updatedFields.push(key);
          payload[key] = currentVal;
        }
      } else if (initialData.current[key] !== contractData[key] && !["updated_by", "updated_at"].includes(key)) {
        updatedFields.push(key);
        payload[key] = contractData[key];
      }
    });

    const initialSow = initialData.current.sow_details ? JSON.parse(initialData.current.sow_details) : [];
    if (JSON.stringify(initialSow) !== JSON.stringify(sowDetails)) {
      updatedFields.push('sow_details');
      payload.sow_details = sowDetails;
    }

    if (updatedFields.length === 0) {
      setSnackbarMessage("No changes to save");
      setIsSuccess(false);
      setSnackbarOpen(true);
      setEditMode(false);
      return;
    }

    try {
      const response = await apiService.postData("update-contract", payload);
      
      if (response.status === "success") {
        setSnackbarMessage(response.message || "Contract updated successfully");
        setIsSuccess(true);
        initialData.current = { ...contractData };
      } else {
        setSnackbarMessage(response.message || "Failed to update contract");
        setIsSuccess(false);
      }
      setSnackbarOpen(true);
      setEditMode(false);
    } catch (err) {
      setSnackbarMessage("Error updating contract: " + (err.response?.data?.message || err.message));
      setIsSuccess(false);
      setSnackbarOpen(true);
      setEditMode(false);
    }
  };

  const toggle = (key) => setOpen(s => ({ ...s, [key]: !s[key] }));

  const isPageLoading = loading || versionLoading;
  const isAutoRenewal = contractData?.renewal_date === "1999-01-01" || contractData?.renewal_date?.startsWith("1999-01-01");

  const renderDatesMetadata = () => (
    <Section title="Dates & Metadata" isOpen={open.datesMetadata} onToggle={() => toggle("datesMetadata")}>
      <Field label="Start Date" value={contractData.start_date} editMode={editMode} onChange={v => handleChange("start_date", v)} type="date" />

      {isAutoRenewal ? (
        <div className="subpage-field">
          <div className="subpage-label">Auto Renewal</div>
          <div className="subpage-value">True</div>
        </div>
      ) : (
        <>
          <Field label="End Date" value={contractData.end_date} editMode={editMode} onChange={v => handleChange("end_date", v)} type="date" />
          <Field label="Renewal Date" value={contractData.renewal_date} editMode={editMode} onChange={v => handleChange("renewal_date", v)} type="date" />
        </>
      )}

      <div><div className="subpage-label">Created At</div><div className="subpage-value">{contractData.created_at || "N/A"}</div></div>
      <div><div className="subpage-label">Updated At</div><div className="subpage-value">{contractData.updated_at || "N/A"}</div></div>

      <div className="subpage-row subpage-row-full">
        <div className="subpage-field">
          <div className="subpage-label">
            <svg width="16" height="16" viewBox="0 0 24 24" fill={contractData.is_favourite ? "#ff6b35" : "none"} stroke="#ff6b35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="subpage-icon">
              <polygon points="12 2 15 9 22 9 17 14 18 21 12 17 6 21 7 14 2 9 9 9 12 2" />
            </svg>
            Is Favourite
          </div>
          <div className="subpage-value">{contractData.is_favourite ? "True" : "False"}</div>
        </div>
        <div className="subpage-field">
          <div className="subpage-label">
            <svg width="16" height="16" viewBox="0 0 24 24" fill={contractData.is_deleted ? "#ff6b35" : "none"} stroke="#ff6b35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="subpage-icon">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
            Is Deleted
          </div>
          <div className="subpage-value">{contractData.is_deleted ? "True" : "False"}</div>
        </div>
      </div>
    </Section>
  );

  return (
    <div className="subpage-card subpage-card--full" style={{ filter: isPageLoading ? 'blur(2px)' : 'none', pointerEvents: isPageLoading ? 'none' : 'auto' }}>
      <Loader isLoading={isPageLoading} message="Loading contract details and version history..." />

      <div className="subpage-tabs">
        <button className={activeTab === "contract" ? "subpage-active" : ""} onClick={() => setActiveTab("contract")}>Details</button>
        <button className={activeTab === "docs" ? "subpage-active" : ""} onClick={() => setActiveTab("docs")}>View Document</button>
        <button className={activeTab === "datatable" ? "subpage-active" : ""} onClick={() => setActiveTab("datatable")}>Version History</button>
      </div>

      {activeTab === "contract" && !fromArchive && hasFullAccess && (
        <div className="subpage-edit-bar">
          {editMode ? (
            <>
              <button onClick={handleSave}>Save</button>
              <button onClick={handleCancel}>Cancel</button>
            </>
          ) : (
            <button onClick={handleEditToggle}>Edit</button>
          )}
        </div>
      )}

      {activeTab === "contract" && (
        <div className="subpage-content">
          {loading && <p>Loading...</p>}
          {!loading && error && (
            <div className="subpage-error-message" style={{ color: "red", padding: "10px", border: "1px solid red", borderRadius: "4px" }}>{error}</div>
          )}
          {!loading && !error && !contractData && (
            <div className="subpage-subpage-placeholder">No contract data available</div>
          )}
          {!loading && !error && contractData && (
            <>
              {/* Contract Details */}
              <Section title="Contract Details" isOpen={open.contractDetails} onToggle={() => toggle("contractDetails")}>
                <Field label="Contract Title" value={contractData.contract_title} editMode={editMode} onChange={v => handleChange("contract_title", v)} />
                <Field label="Description" value={contractData.description} editMode={editMode} onChange={v => handleChange("description", v)} type="textarea" />
                <SelectField
                  label="Contract Type"
                  value={contractData.contract_type_id}
                  editMode={editMode}
                  onChange={v => handleChange("contract_type_id", v)}
                  options={contractTypeOptions}
                  placeholder="Select contract type..."
                />
                <div><div className="subpage-label">Creation Type</div><div className="subpage-value">{contractData.creation_type || "N/A"}</div></div>
                <div><div className="subpage-label">Parent Contract</div><div className="subpage-value">{contractData.parent_contract_title || "N/A"}</div></div>
                <div>
                  <div className="subpage-label">Template Name</div>
                  <div className="subpage-value">
                    {templateOptions.find(t => t.value === contractData.template_id)?.label || contractData.template_name || "N/A"}
                  </div>
                </div>
                <div><div className="subpage-label">SharePoint Name</div><div className="subpage-value">{contractData.sharepointName || "N/A"}</div></div>
                <div><div className="subpage-label">SharePoint Path</div><div className="subpage-value">{contractData.sharepointPath || "N/A"}</div></div>
              </Section>

              {/* Client & Status */}
              <Section title="Client & Status" isOpen={open.clientStatus} onToggle={() => toggle("clientStatus")}>
                <SelectField
                   label="Region"
                   value={contractData.region_id}
                   editMode={editMode}
                   onChange={v => handleChange("region_id", v)}
                   options={regions.map(r => ({ label: r.value, value: r.id }))}
                   placeholder="Select region..."
                   contractData={contractData}
                />

                <SelectField
                  label="Client Name"
                  value={contractData.party_id}
                  editMode={editMode}
                  onChange={v => handleChange("party_id", v)}
                  options={clientOptions}
                  placeholder="Select client..."
                />
                <SelectField
                  label="Project Name"
                  value={contractData.project_id}
                  editMode={editMode}
                  onChange={v => handleChange("project_id", v)}
                  options={projectOptions}
                  placeholder="Select project..."
                  fallbackLabel={contractData.project_name}
                />
                <div><div className="subpage-label">Created By</div><div className="subpage-value">{contractData.created_by_name || "N/A"}</div></div>
                <div><div className="subpage-label">Updated By</div><div className="subpage-value">{contractData.updated_by_name || "N/A"}</div></div>
                <div><div className="subpage-label">Contract Status</div><div className="subpage-value">{contractData.contract_status || "N/A"}</div></div>
                <div>
                  <div className="subpage-label">Owner Department</div>
                  <div className="subpage-value">
                    {departments.find(d => String(d.id) === String(contractData.owner_department_id))?.value || "N/A"}
                  </div>
                </div>
              </Section>

{renderDatesMetadata()}

              {/* Billing & Metadata - Admin Only */}
              <Section title="Billing & Metadata" isOpen={open.billingMetadata} onToggle={() => toggle("billingMetadata")}>
                <SelectField
                  label="Billing Country"
                  value={contractData.billing_country}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("billing_country", v)}
                  options={billingCountries}
                  placeholder="Select billing country..."
                />
                <SelectField
                  label="Billing Type"
                  value={contractData.billing_type}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("billing_type", v)}
                  options={billingTypes}
                  placeholder="Select billing type..."
                />
                <SelectField
                  label="Execution Type"
                  value={contractData.execution_type}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("execution_type", v)}
                  options={executionTypes}
                  placeholder="Select execution type..."
                  multiSelect={true}
                />
                <SelectField
                  label="Bill Rate Currency"
                  value={contractData.bill_rate_currency}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("bill_rate_currency", v)}
                  options={currencies}
                  placeholder="Select currency..."
                />
                <SelectField
                  label="Pay Rate Currency"
                  value={contractData.pay_rate_currency}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("pay_rate_currency", v)}
                  options={payRateCurrencies}
                  placeholder="Select currency..."
                />
                <SelectField
                  label="Net Term"
                  value={contractData.net_term}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("net_term", v)}
                  options={netTermOptions}
                  placeholder="Select net term..."
                />
                <Field
                  label="PO Number"
                  value={contractData.po_no}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("po_no", v)}
                />
                <Field
                  label="Signature Status"
                  value={contractData.signature_status}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("signature_status", v)}
                />
                <Field
                  label="Whose Paper"
                  value={contractData.whose_paper}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("whose_paper", v)}
                />
                <SelectField
                  label="Practice Name"
                  value={contractData.practice_name}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("practice_name", v)}
                  options={departments.map(d => ({ label: d.value, value: d.id }))}
                  placeholder="Select practice name..."
                />
                <Field
                  label="Metatag"
                  value={contractData.metatag}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("metatag", v)}
                />
                <Field
                  label="Agreement Owner"
                  value={contractData.agreement_owner}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("agreement_owner", v)}
                />
                <Field
                  label="Source"
                  value={contractData.source}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("source", v)}
                />
                <div>
                  <div className="subpage-label">Effective Date</div>
                  {editMode && userRole === 'admin' ? (
                    <input
                      type="date"
                      value={contractData.effective_date || ""}
                      onChange={(e) => handleChange("effective_date", e.target.value)}
                      style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                    />
                  ) : (
                    <div className="subpage-value">{contractData.effective_date || "N/A"}</div>
                  )}
                </div>
                <Field
                  label="CR Entity"
                  value={contractData.cr_entity}
                  editMode={editMode && userRole === 'admin'}
                  onChange={v => handleChange("cr_entity", v)}
                />
                {sowDetails.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div className="subpage-label" style={{ marginBottom: '12px' }}>SOW Details</div>
                    {editMode && userRole === 'admin' ? (
                      <div>
                        {sowDetails.map((detail, index) => (
                          <div key={index} style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr 1fr 1fr 70px', 
                            gap: '12px', 
                            marginBottom: '12px',
                            padding: '16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            backgroundColor: '#f8fafc',
                            alignItems: 'end'
                          }}>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Role Name</label>
                              <input
                                type="text"
                                value={detail.role_name || ''}
                                onChange={(e) => {
                                  const newDetails = [...sowDetails];
                                  newDetails[index].role_name = e.target.value;
                                  setSowDetails(newDetails);
                                }}
                                placeholder="Enter Role"
                                style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', margin: '0' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>Role Rate</label>
                              <input
                                type="number"
                                value={detail.role_rate || ''}
                                onChange={(e) => {
                                  const newDetails = [...sowDetails];
                                  newDetails[index].role_rate = e.target.value;
                                  setSowDetails(newDetails);
                                }}
                                placeholder="Enter Rate"
                                style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', margin: '0' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>SOW Amount</label>
                              <input
                                type="number"
                                value={detail.sow_amount || ''}
                                onChange={(e) => {
                                  const newDetails = [...sowDetails];
                                  newDetails[index].sow_amount = e.target.value;
                                  setSowDetails(newDetails);
                                }}
                                placeholder="Enter Amount"
                                style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', margin: '0' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', display: 'block', fontWeight: '500' }}>SOW Hours</label>
                              <input
                                type="number"
                                value={detail.sow_hours || ''}
                                onChange={(e) => {
                                  const newDetails = [...sowDetails];
                                  newDetails[index].sow_hours = e.target.value;
                                  setSowDetails(newDetails);
                                }}
                                placeholder="Enter Hours"
                                style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', margin: '0' }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              {index === sowDetails.length - 1 && (
                                <button
                                  type="button"
                                  onClick={() => setSowDetails([...sowDetails, { role_name: '', role_rate: '', sow_amount: '', sow_hours: '' }])}
                                  style={{
                                    backgroundColor: '#f8fafc',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e2e8f0';
                                    e.currentTarget.style.borderColor = '#94a3b8';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8fafc';
                                    e.currentTarget.style.borderColor = '#cbd5e1';
                                  }}
                                  title="Add Row"
                                >
                                  +
                                </button>
                              )}
                              {sowDetails.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newDetails = sowDetails.filter((_, i) => i !== index);
                                    setSowDetails(newDetails);
                                  }}
                                  style={{
                                    backgroundColor: '#f8fafc',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e2e8f0';
                                    e.currentTarget.style.borderColor = '#94a3b8';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8fafc';
                                    e.currentTarget.style.borderColor = '#cbd5e1';
                                  }}
                                  title="Remove Row"
                                >
                                  −
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8fafc' }}>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Role Name</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Role Rate</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>SOW Amount</th>
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>SOW Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sowDetails.map((detail, idx) => (
                            <tr key={idx}>
                              <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{detail.role_name || 'N/A'}</td>
                              <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{detail.role_rate || 'N/A'}</td>
                              <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{detail.sow_amount || 'N/A'}</td>
                              <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{detail.sow_hours || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </Section>
            </>
          )}
        </div>
      )}

      {/* Document Viewer */}
      {activeTab === "docs" && (
        <div className="subpage-content" style={{ height: "100%", minHeight: "400px" }}>
          {contractData?.file_url ? (
            (() => {
              const isPDF = /\.(pdf)$/i.test(contractData.sharepointName || contractData.file_url);
              if (isPDF) {
                let pdfViewerUrl = contractData.file_url;
                if (pdfViewerUrl.includes('sharepoint.com') && !pdfViewerUrl.includes('embed.aspx')) {
                  const match = pdfViewerUrl.match(/(https:\/\/[^/]+\.sharepoint\.com\/[^?]+)/);
                  if (match) pdfViewerUrl = `${match[1]}?action=embedview`;
                }
                return (
                  <div style={{ width: "100%", height: "100%", border: "1px solid #ccc", borderRadius: "4px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa", gap: "20px" }}>
                    <div style={{ textAlign: "center", maxWidth: "500px", padding: "20px" }}>
                      <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" strokeWidth="2" style={{ marginBottom: "20px" }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                      <h3 style={{ color: "#333", marginBottom: "10px", fontSize: "20px" }}>PDF Document</h3>
                      <p style={{ color: "#666", marginBottom: "20px", lineHeight: "1.6" }}>{contractData.sharepointName || "PDF file"}</p>
                      <button
                        onClick={() => window.open(pdfViewerUrl, '_blank', 'noopener,noreferrer')}
                        style={{ backgroundColor: "#ff6b35", color: "white", border: "none", padding: "12px 32px", borderRadius: "6px", fontSize: "16px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        Open PDF Document
                      </button>
                      <p style={{ color: "#999", fontSize: "13px", marginTop: "15px" }}>Opens in a new window with SharePoint authentication</p>
                    </div>
                  </div>
                );
              }
              return <iframe src={contractData.file_url} width="100%" height="100%" title="Document Preview" style={{ border: "1px solid #ccc", borderRadius: "4px" }} />;
            })()
          ) : (
            <p>No documentation available for this contract.</p>
          )}
        </div>
      )}

      {/* Version History */}
      {activeTab === "datatable" && (
        <div className="subpage-content" style={{ height: "100%", minHeight: "400px" }}>
          <DataTable value={versionData} paginator rows={5} rowsPerPageOptions={[5, 10, 20, 50]} tableStyle={{ width: '100%', border: '1px solid #ccc' }}>
            <Column field="id" header="Version ID" />
            <Column field="lastModifiedDateTime" header="Last Modified Date" body={row => new Date(row.lastModifiedDateTime).toLocaleString()} />
            <Column header="Modified By" body={row => row.lastModifiedBy?.user?.displayName || "N/A"} />
            <Column header="Email" body={row => row.lastModifiedBy?.user?.email || "N/A"} />
            <Column header="Size (KB)" body={row => row.size ? (row.size / 1024).toFixed(1) : "—"} />
            <Column header="Action" body={row => (
              <button onClick={() => window.open(row["@microsoft.graph.downloadUrl"], "_blank")} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ff6b35", display: "flex", alignItems: "center", gap: "4px" }} title="Download this version">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 15v4m18-4v4M8 12l4 4 4-4M12 2v14" />
                </svg>
                Download
              </button>
            )} />
          </DataTable>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        sx={{ '& .MuiSnackbarContent-root': { backgroundColor: isSuccess ? '#4caf50' : '#f44336' }, zIndex: 1400 }}
      />
    </div>
  );
};

SubPage.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
  fromArchive: PropTypes.bool,
};

export default SubPage;